import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchBar } from '../components/SearchBar';
import { FilterPills, type FilterState } from '../components/FilterPills';
import { ProductCard } from '../components/ProductCard';
import { SEO } from '../components/SEO';
import { useProducts, type ProductFilters } from '../hooks/useProducts';
import type { Brand } from '@hygh-vybe/lib';
import './ExplorePage.css';
import { API_BASE } from '../config';

function parsePotencyRange(potency: string | null): { min: number | null; max: number | null } {
  if (!potency) return { min: null, max: null };
  const [minStr, maxStr] = potency.split('-');
  return {
    min: Number(minStr) || null,
    max: Number(maxStr) || null,
  };
}

export function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(searchParams.get('brand'));
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [brandsMap, setBrandsMap] = useState<Map<string, Brand>>(new Map());
  const [filterState, setFilterState] = useState<FilterState>({
    format: null,
    potency: null,
    effect: null,
    buyable: searchParams.get('buyable') === 'true',
  });

  const sentinelRef = useRef<HTMLDivElement>(null);

  // Fetch brand list for dropdown — try /v1/brands first, fall back to extracting from products
  useEffect(() => {
    async function fetchBrands() {
      try {
        // Try dedicated brands endpoint first
        const brandsRes = await fetch(`${API_BASE}/v1/brands`);
        if (brandsRes.ok) {
          const brandsJson = await brandsRes.json();
          const allBrands = brandsJson.data || [];
          const brandNames = allBrands
            .map((b: Brand) => b.brandName)
            .filter(Boolean) as string[];

          if (brandNames.length > 0) {
            brandNames.sort((a, b) => a.localeCompare(b));
            setBrandOptions(brandNames);

            // Build lookup map for brand header display
            const map = new Map<string, Brand>();
            allBrands.forEach((b: Brand) => {
              if (b.brandName) map.set(b.brandName, b);
            });
            setBrandsMap(map);
            return;
          }
        }
        // Fall back: extract unique brand names from products
        const res = await fetch(`${API_BASE}/v1/products?limit=500`);
        if (!res.ok) return;
        const json = await res.json();
        const products = json.data || [];
        const brands = [...new Set(products.map((p: { brandName?: string }) => p.brandName).filter(Boolean))] as string[];
        brands.sort((a, b) => a.localeCompare(b));
        setBrandOptions(brands);
      } catch {
        // Non-critical — dropdown will just be empty
      }
    }
    fetchBrands();
  }, []);

  // Validate and set search query
  const handleSearch = useCallback((query: string) => {
    const trimmed = query.trim();
    if (query.length > 0 && trimmed.length === 0) {
      setSearchError('Please enter a valid search term');
      setSearchQuery('');
      return;
    }
    setSearchError(null);
    setSearchQuery(trimmed);
  }, []);

  const handleInputChange = useCallback((value: string) => {
    setSearchInput(value);
    // Clear error when user starts typing valid content
    if (value.trim().length > 0 || value.length === 0) {
      setSearchError(null);
    }
  }, []);

  const handleFilterToggle = useCallback((category: keyof FilterState, value: string) => {
    setFilterState((prev) => ({
      ...prev,
      [category]: prev[category] === value ? null : value,
    }));
  }, []);

  const handleToggleBuyable = useCallback(() => {
    setFilterState((prev) => ({
      ...prev,
      buyable: !prev.buyable,
    }));
  }, []);

  const potency = parsePotencyRange(filterState.potency);

  const filters: ProductFilters = {
    query: searchQuery,
    brand: selectedBrand,
    format: filterState.format,
    potencyMin: potency.min,
    potencyMax: potency.max,
    effect: filterState.effect,
    buyable: filterState.buyable,
  };

  const { products, loading, loadingMore, error, hasMore, loadMore, suggestions, hasFetched } =
    useProducts(filters);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadMore]);

  const showNoResults = hasFetched && !loading && !loadingMore && !error && products.length === 0 && (searchQuery || selectedBrand || filterState.format || filterState.potency || filterState.effect || filterState.buyable);

  return (
    <div className="explore-page">
      <SEO
        title={selectedBrand ? `${selectedBrand} THC Drinks` : 'Explore THC Drinks & Hemp Beverages'}
        description={selectedBrand ? `Browse and buy ${selectedBrand} THC beverages. Real ratings and reviews from the community.` : 'Explore hundreds of THC drinks. Filter by potency, format, flavor, and effect. Find your perfect hemp beverage.'}
        canonical="/thc-drinks"
      />

      {searchParams.get('mode') === 'review' && (
        <div className="explore-page__review-banner">
          <strong>Ready to review?</strong> Find your drink below, tap it, and scroll down to leave your rating and review.
        </div>
      )}

      <div className="explore-page__search-row">
        <SearchBar
          value={searchInput}
          onChange={handleInputChange}
          onSearch={handleSearch}
          error={searchError}
        />
        <select
          className="explore-page__brand-select"
          value={selectedBrand || ''}
          onChange={(e) => {
            const brand = e.target.value || null;
            setSelectedBrand(brand);
            // Update URL without reload
            if (brand) {
              setSearchParams({ brand });
              setFilterState({ format: null, potency: null, effect: null, buyable: false });
            } else {
              setSearchParams({});
            }
          }}
          aria-label="Filter by brand"
        >
          {brandOptions.length === 0 ? (
            <option value="">Loading brands...</option>
          ) : (
            <>
              <option value="">All Brands</option>
              {brandOptions.map((brand) => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </>
          )}
        </select>
      </div>

      <FilterPills filters={filterState} onToggle={handleFilterToggle} onToggleBuyable={handleToggleBuyable} />

      {/* Brand header when a specific brand is selected */}
      {selectedBrand && brandsMap.has(selectedBrand) && (() => {
        const brand = brandsMap.get(selectedBrand)!;
        return (
          <div className="explore-page__brand-header">
            {brand.logoUrl && (
              <img
                src={brand.logoUrl}
                alt={brand.brandName}
                className="explore-page__brand-logo"
              />
            )}
            <div className="explore-page__brand-info">
              <h2 className="explore-page__brand-name">{brand.brandName}</h2>
              {brand.description && (
                <p className="explore-page__brand-desc">{brand.description}</p>
              )}
            </div>
          </div>
        );
      })()}

      {error && (
        <div className="explore-page__error" role="alert">
          <p>{error}</p>
        </div>
      )}

      {showNoResults && (
        <div className="explore-page__empty" role="status">
          <p className="explore-page__empty-title">No results found</p>
          <p className="explore-page__empty-subtitle">
            Try adjusting your search or filters
          </p>
          {suggestions.length > 0 && (
            <div className="explore-page__suggestions">
              <p className="explore-page__suggestions-label">Try searching for:</p>
              <div className="explore-page__suggestions-list">
                {suggestions.slice(0, 3).map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="explore-page__suggestion-btn"
                    onClick={() => {
                      setSearchInput(suggestion);
                      handleSearch(suggestion);
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {products.length > 0 && (
        <div className="explore-page__grid" role="feed" aria-label="Product results">
          {products.map((product) => (
            <ProductCard key={product.productId} product={product} />
          ))}
        </div>
      )}

      {loading && (
        <div className="explore-page__loading" aria-label="Searching products">
          <svg
            className="explore-page__leaf-icon"
            viewBox="0 0 64 64"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M32 56V32" />
            <path d="M32 32C32 32 20 28 16 16C16 16 28 20 32 32Z" />
            <path d="M32 32C32 32 44 28 48 16C48 16 36 20 32 32Z" />
            <path d="M32 32C32 32 24 20 28 8C28 8 32 20 32 32Z" />
            <path d="M32 32C32 32 40 20 36 8C36 8 32 20 32 32Z" />
            <path d="M32 32C32 32 14 24 8 12C8 12 20 20 32 32Z" />
            <path d="M32 32C32 32 50 24 56 12C56 12 44 20 32 32Z" />
          </svg>
          <p className="explore-page__loading-text">Searching...</p>
        </div>
      )}

      {loadingMore && (
        <div className="explore-page__loading-more" aria-label="Loading more products">
          <div className="explore-page__spinner explore-page__spinner--small" />
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="explore-page__sentinel" aria-hidden="true" />
    </div>
  );
}
